import { Fieldset } from "@base-ui/react/fieldset";
import { Radio as BaseRadio } from "@base-ui/react/radio";
import {
  RadioGroup as BaseRadioGroup,
  type RadioGroupChangeEventDetails,
} from "@base-ui/react/radio-group";
import classNames from "classnames";
import * as React from "react";

import { Field } from "../Field";

import styles from "./RadioGroup.module.css";

/**
 * How the options are laid out. `"list"` stacks them as plain rows;
 * `"card"` renders each as a bordered, selectable card in a responsive grid.
 */
export type RadioGroupAppearance = "list" | "card";

/**
 * Details accompanying a change — most usefully `reason`, which says what
 * caused it.
 */
export type RadioChangeEventDetails = RadioGroupChangeEventDetails;

/** How big the dot and the option text are. */
export type RadioGroupSize = "sm" | "md";

const sizeClass: Record<RadioGroupSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
};

/**
 * What the items read from their group: the appearance, so a card knows to
 * render its own surface, and the size.
 */
const RadioGroupItemContext = React.createContext<{
  appearance: RadioGroupAppearance;
  size: RadioGroupSize;
}>({ appearance: "list", size: "sm" });

export interface RadioGroupProps<T> {
  /** The chosen value, or `null` when nothing is chosen. Controlled. */
  value?: T | null;

  /** Fired with the newly chosen value, and Base UI's event details. */
  onValueChange?: (value: T, eventDetails: RadioChangeEventDetails) => void;

  /** Uncontrolled starting value. */
  defaultValue?: T;

  /** Names the group, as a fieldset legend. */
  legend?: React.ReactNode;

  /** Helper text under the legend. */
  description?: React.ReactNode;

  /** Group error text; also marks the options invalid. */
  error?: string;

  /**
   * Whether the options are plain rows or bordered cards.
   * @default "list"
   */
  appearance?: RadioGroupAppearance;

  /**
   * The dot and option text size.
   * @default "sm"
   */
  size?: RadioGroupSize;

  disabled?: boolean;

  /** Identifies the field in a native form. */
  name?: string;

  className?: string;

  /** Accessible name when there is no visible `legend`. */
  "aria-label"?: string;
  /** Id of an element that names the group, such as its section's heading. */
  "aria-labelledby"?: string;

  children?: React.ReactNode;
}

/**
 * A set of mutually exclusive options.
 *
 * Generic over the value, so an enum or a string union types both the group
 * and its items.
 *
 * @example
 * ```tsx
 * <RadioGroup<TriggerSourceType>
 *   aria-label="Fires on"
 *   appearance="card"
 *   value={source}
 *   onValueChange={setSource}
 * >
 *   <Radio value={TriggerSourceType.Events} label="On a change" description="Fire when one entity's data changes." />
 *   <Radio value={TriggerSourceType.Schedule} label="On a schedule" description="Fire on a cron schedule." />
 * </RadioGroup>
 * ```
 */
export function RadioGroup<T>({
  value,
  onValueChange,
  defaultValue,
  legend,
  description,
  error,
  appearance = "list",
  size = "sm",
  disabled = false,
  name,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  children,
}: RadioGroupProps<T>) {
  const legendId = React.useId();
  const itemContext = React.useMemo(
    () => ({ appearance, size }),
    [appearance, size],
  );

  const group = (
    <BaseRadioGroup<T | null>
      className={classNames(styles.group, styles[appearance])}
      value={value}
      // Nothing in the group can clear the choice once one is made, so the
      // reported value is always a real `T` even though the group's own may
      // start as null.
      onValueChange={(next, eventDetails) => {
        onValueChange?.(next as T, eventDetails);
      }}
      defaultValue={defaultValue}
      disabled={disabled}
      name={name}
      aria-label={legend === undefined ? ariaLabel : undefined}
      aria-labelledby={legend === undefined ? ariaLabelledBy : legendId}
    >
      <RadioGroupItemContext.Provider value={itemContext}>
        {children}
      </RadioGroupItemContext.Provider>
    </BaseRadioGroup>
  );

  const described = (
    <>
      {description !== undefined && (
        <Field.Description>{description}</Field.Description>
      )}
      {group}
      {error !== undefined && <Field.Error match>{error}</Field.Error>}
    </>
  );

  return (
    <Field.Root
      className={classNames(styles.field, className)}
      name={name}
      invalid={error !== undefined}
      disabled={disabled}
    >
      {legend === undefined ? (
        described
      ) : (
        // The legend names the fieldset on its own; the id also points the
        // radiogroup at it, so the set of options carries the name too.
        <Fieldset.Root className={styles.fieldset}>
          <Fieldset.Legend id={legendId} className={styles.legend}>
            {legend}
          </Fieldset.Legend>
          {described}
        </Fieldset.Root>
      )}
    </Field.Root>
  );
}

export interface RadioProps<T> {
  /** What the group reports when this option is chosen. */
  value: T;

  /** The option's title. */
  label?: React.ReactNode;

  /** A second line; in card appearance, the card's blurb. */
  description?: React.ReactNode;

  /** Card appearance only: the glyph the card leads with. */
  icon?: React.ReactNode;

  disabled?: boolean;

  /** Reason the option is disabled, shown on hover. */
  title?: string;

  className?: string;

  /** Accessible name when there is no visible `label`. */
  "aria-label"?: string;
}

/**
 * One option of a {@link RadioGroup}. The group decides whether it renders as
 * a plain row or as a card.
 */
export function Radio<T>({
  value,
  label,
  description,
  icon,
  disabled = false,
  title,
  className,
  "aria-label": ariaLabel,
}: RadioProps<T>) {
  const { appearance, size } = React.useContext(RadioGroupItemContext);

  const dot = (
    <BaseRadio.Root
      className={styles.dot}
      value={value}
      disabled={disabled}
      aria-label={label === undefined ? ariaLabel : undefined}
    >
      <BaseRadio.Indicator className={styles.indicator} />
    </BaseRadio.Root>
  );

  return (
    <Field.Item
      className={classNames(
        styles.item,
        styles[appearance],
        sizeClass[size],
        className,
      )}
      title={title}
    >
      {label === undefined ? (
        dot
      ) : (
        <Field.Label className={styles.labelRow}>
          {dot}
          {icon !== undefined && appearance === "card" && (
            <span className={styles.icon}>{icon}</span>
          )}
          <span className={styles.labelText}>{label}</span>
        </Field.Label>
      )}
      {description !== undefined && (
        <Field.Description className={styles.description}>
          {description}
        </Field.Description>
      )}
    </Field.Item>
  );
}
